import { Camera, AlertTriangle, MessageSquare, Play, RefreshCw, Check, Loader2 } from 'lucide-react';
import { Badge } from '../../../components/ui/badge';

export const PatientHeaderBar = ({ patient, recordingMode, isGenerating, soapContent, onShowLens, onStartSession, fhirStatus, onFhirSync }) => {
  return (
    <div style={{ background: '#fff', borderBottom: '0.5px solid #e8e8e8', padding: '14px 20px', borderRadius: '12px', marginBottom: 12 }}
      data-testid="patient-header">
      <div className="flex items-center justify-between">
        {/* Left: avatar + info */}
        <div className="flex items-center gap-3">
          <div style={{
            width: 38, height: 38, borderRadius: '50%', background: '#e8e8e8',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 13, fontWeight: 500, color: '#555',
          }}>
            {patient.firstName?.[0]}{patient.lastName?.[0]}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span style={{ fontSize: 16, fontWeight: 500, color: '#111' }}>{patient.firstName} {patient.lastName}</span>
              <span style={{ fontSize: 11, color: '#aaaaaa', background: '#f5f5f5', borderRadius: 4, padding: '1px 6px' }}>{patient.mrn}</span>
            </div>
            <div className="flex items-center gap-1.5" style={{ marginTop: 2 }}>
              <span style={{ fontSize: 12, color: '#888888' }}>
                {patient.gender} &middot; {patient.age}y &middot; DOB {patient.dob} &middot; Encounter: {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </span>
              {patient.insurance && (
                <span style={{ fontSize: 11, color: '#555', background: '#f0f0f0', borderRadius: 4, padding: '1px 6px' }}>{patient.insurance}</span>
              )}
            </div>
          </div>
        </div>

        {/* Right: action buttons */}
        <div className="flex items-center gap-2">
          {/* FHIR Sync indicator */}
          {fhirStatus && fhirStatus.fhir_enabled && (
            <div className="flex items-center gap-1.5" data-testid="fhir-indicator">
              {fhirStatus.sync_status === 'syncing' && (
                <Badge variant="outline" className="text-[9px] border-blue-200 text-blue-600 flex items-center gap-1 animate-pulse">
                  <Loader2 className="h-2.5 w-2.5 animate-spin" /> Syncing EHR
                </Badge>
              )}
              {fhirStatus.sync_status === 'synced' && (
                <Badge variant="outline" className="text-[9px] border-emerald-200 text-emerald-600 flex items-center gap-1">
                  <Check className="h-2.5 w-2.5" /> FHIR Synced
                </Badge>
              )}
              {fhirStatus.sync_status === 'partial' && (
                <Badge variant="outline" className="text-[9px] border-amber-200 text-amber-600 flex items-center gap-1">
                  <AlertTriangle className="h-2.5 w-2.5" /> Partial Sync
                </Badge>
              )}
              {(fhirStatus.sync_status === 'failed' || fhirStatus.sync_status === 'empty') && (
                <Badge variant="outline" className="text-[9px] border-red-200 text-red-500 flex items-center gap-1">
                  Sync Failed
                </Badge>
              )}
            </div>
          )}
          {fhirStatus?.fhir_enabled && onFhirSync && (
            <button onClick={onFhirSync}
              disabled={fhirStatus?.sync_status === 'syncing'}
              style={{ height: 32, padding: '0 12px', border: '0.5px solid #e0e0e0', borderRadius: 7, fontSize: 12, color: '#555', background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
              data-testid="fhir-sync-btn">
              <RefreshCw className={`h-3.5 w-3.5 ${fhirStatus?.sync_status === 'syncing' ? 'animate-spin' : ''}`} /> Sync EHR
            </button>
          )}
          <button onClick={onShowLens}
            style={{ height: 32, padding: '0 12px', border: '0.5px solid #e0e0e0', borderRadius: 7, fontSize: 12, color: '#555', background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
            data-testid="lens-btn">
            <Camera className="h-3.5 w-3.5" /> Lens
          </button>
          {!recordingMode && !isGenerating && !soapContent && (
            <>
              <button onClick={() => onStartSession('dictation')}
                style={{ height: 32, padding: '0 12px', border: '0.5px solid #e0e0e0', borderRadius: 7, fontSize: 12, color: '#555', background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
                data-testid="start-dictation-btn">
                <MessageSquare className="h-3.5 w-3.5" /> Dictation
              </button>
              <button onClick={() => onStartSession('ambient')}
                style={{ height: 32, padding: '0 12px', borderRadius: 7, fontSize: 12, fontWeight: 500, color: '#fff', background: '#111111', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, border: 'none' }}
                data-testid="start-ambient-btn">
                <Play className="h-3 w-3" fill="currentColor" /> Start Visit
              </button>
            </>
          )}
        </div>
      </div>

      {/* Allergy Alert */}
      {patient.allergies?.some(a => a.severity === 'Severe') && (
        <div style={{
          background: '#fef2f2', border: '0.5px solid #fca5a5', borderRadius: 7,
          padding: '7px 12px', marginTop: 10,
          display: 'flex', alignItems: 'center', gap: 8,
        }} data-testid="allergy-banner">
          <AlertTriangle style={{ width: 12, height: 12, color: '#b91c1c', flexShrink: 0 }} />
          <span style={{ fontSize: 12, color: '#b91c1c' }}>
            Allergy alert: {patient.allergies.filter(a => a.severity === 'Severe').map(a => `${a.allergen} — ${a.reaction}`).join(', ')}
          </span>
        </div>
      )}
    </div>
  );
};
